import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { FiMic, FiPlay, FiSend, FiSquare, FiTrash2 } from 'react-icons/fi';
import { cUploadAudio } from '../../api';
import { useLanguage } from '../../i18n';
import { startVoiceRecording } from '../../utils/audio';
import { BLUE } from './brand';

/**
 * Record a voice note with the microphone and hand the uploaded MP3 URL back.
 *
 * Used by comments ("voice comment") and by chat ("voice message"): press the
 * mic → record (pulsing red dot + timer) → Send (uploads + compresses) or
 * Discard. Hidden automatically when the browser cannot record.
 */
export default function VoiceRecorder({ kind = 'voice', onUploaded, onCancel, compact = false }) {
  const { t } = useLanguage();
  const [recorder, setRecorder] = useState(null);
  const [seconds, setSeconds] = useState(0);
  const [clip, setClip] = useState(null);       // { url, file, seconds }
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  const supported = useRef(true);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia
        || typeof MediaRecorder === 'undefined') {
      supported.current = false;
    }
    return () => {
      clearInterval(timer.current);
      if (recorder) recorder.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!supported.current) return null;

  const start = async () => {
    const rec = startVoiceRecording();
    if (!rec) { toast.error(t('micNotSupported')); return; }
    try {
      await rec.start();
      setRecorder(rec);
      setSeconds(0);
      clearInterval(timer.current);
      timer.current = setInterval(() => setSeconds(rec.seconds), 500);
    } catch (e) {
      toast.error(t('micDenied'));
    }
  };

  const stop = async () => {
    clearInterval(timer.current);
    if (!recorder) return;
    const result = await recorder.stop();
    setRecorder(null);
    if (!result) { toast.error(t('nothingRecorded')); return; }
    setClip(result);
  };

  const discard = () => {
    clearInterval(timer.current);
    if (recorder) recorder.cancel();
    setRecorder(null);
    setClip(null);
    setSeconds(0);
    if (onCancel) onCancel();
  };

  const send = async () => {
    if (!clip) return;
    setBusy(true);
    try {
      const res = await cUploadAudio(clip.file, kind);
      toast.success(`${t('voiceSent')} · ${Math.round((res.size || 0) / 1024)} KB`);
      const payload = { url: res.url, duration: res.duration || clip.seconds };
      setClip(null);
      setSeconds(0);
      if (onUploaded) onUploaded(payload);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    } finally {
      setBusy(false);
    }
  };

  // recording in progress
  if (recorder) {
    return (
      <span className="inline-flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5 text-xs font-bold text-red-600">
          <span className="w-2.5 h-2.5 rounded-full bg-red-600 rec-pulse" />
          {String(Math.floor(seconds / 60))}:{String(seconds % 60).padStart(2, '0')}
        </span>
        <button
          type="button"
          onClick={stop}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-white text-xs font-bold bg-red-600"
        >
          <FiSquare className="w-3.5 h-3.5" /> {t('stopRecording')}
        </button>
        <button
          type="button"
          onClick={discard}
          className="p-1.5 rounded-full text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
          aria-label={t('cancel')}
        >
          <FiTrash2 className="w-4 h-4" />
        </button>
      </span>
    );
  }

  // clip recorded, waiting to be sent
  if (clip) {
    return (
      <span className="inline-flex items-center gap-2">
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <audio src={clip.url} controls className="h-8 max-w-[190px]" />
        <button
          type="button"
          onClick={send}
          disabled={busy}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-white text-xs font-bold disabled:opacity-60"
          style={{ background: BLUE }}
        >
          <FiSend className="w-3.5 h-3.5" /> {busy ? t('uploading') : t('sendVoice')}
        </button>
        <button
          type="button"
          onClick={discard}
          className="p-1.5 rounded-full text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
          aria-label={t('delete')}
        >
          <FiTrash2 className="w-4 h-4" />
        </button>
      </span>
    );
  }

  return (
    <button
      type="button"
      data-sfx="record"
      onClick={start}
      title={t('recordVoice')}
      className={`inline-flex items-center gap-1.5 rounded-full font-bold text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 ${
        compact ? 'p-2.5' : 'px-3 py-2 text-xs border border-gray-200 dark:border-gray-700'
      }`}
    >
      {compact ? <FiMic className="w-5 h-5" /> : <><FiPlay className="w-3.5 h-3.5" /> {t('recordVoice')}</>}
      {!compact && <FiMic className="w-3.5 h-3.5" />}
    </button>
  );
}
